/**
 * Build the nginx forward-auth snippet for one app.
 *
 * The operator pastes this into their app server's nginx config; on
 * reload, nginx makes a subrequest to the portal's `/v1/portal/auth-check`
 * before every page hit. 200 from the portal lets the request through;
 * 401 is handled differently depending on the client mode:
 *
 *   mode = "browser"      → 401 → 302 to portal login (web flow)
 *   mode = "api"          → 401 → plain JSON 401 (mobile/API flow,
 *                           no redirect — the app handles re-auth)
 *   mode = "hybrid"       → both: API paths get JSON 401, web paths
 *                           get the redirect. Default for mixed apps
 *                           where REST endpoints sit at /api/* and
 *                           the SPA / pages live at /.
 *
 * The subrequest carries BOTH Cookie AND Authorization headers so
 * the gate works for browsers (Cookie: urltoken=…) and native mobile
 * apps (Authorization: Bearer <JWT>) without separate endpoints.
 *
 *   portalBase  — e.g. https://portal.smarthatch.com
 *   appUrl      — the app URL the user just clicked "Lock down" on;
 *                 only used to derive the host comment.
 *   mode        — "browser" | "api" | "hybrid" (default "hybrid")
 *   apiPrefix   — for "hybrid", what path prefix counts as API.
 *                 Default "/api/" — adjust for /v1/, /rest/, etc.
 */
export function renderNginxAuthSnippet({ portalBase, appUrl, mode = "hybrid", apiPrefix = "/api/" }) {
  const host = safeHost(appUrl);
  const cleanBase = String(portalBase || "").replace(/\/+$/, "");
  const showBrowser = mode === "browser" || mode === "hybrid";
  const showApi = mode === "api" || mode === "hybrid";

  const gateBlock = `# 1. The gate. Forwards the user's session token to the portal.
#    Cookie covers browsers; Authorization covers mobile / API clients
#    that ship the JWT as a Bearer token instead of a cookie.
location = /__portal_auth_check {
    internal;
    proxy_pass         ${cleanBase}/v1/portal/auth-check;
    proxy_pass_request_body off;
    proxy_set_header   Content-Length      "";
    proxy_set_header   Cookie              $http_cookie;
    proxy_set_header   Authorization       $http_authorization;
    proxy_set_header   X-Original-Host     $host;
    proxy_set_header   X-Original-URI      $request_uri;
    proxy_set_header   X-Original-Proto    $scheme;
    proxy_intercept_errors on;
}`;

  const loginBlock = showBrowser ? `

# 2. Browser-flow 401 handler: 302 to portal login with ?next so the
#    user bounces back to the original URL after signing in.
location @portal_login {
    return 302 ${cleanBase}/?next=$scheme://$host$request_uri;
}` : "";

  const apiBlock = showApi ? `

# ${showBrowser ? "3" : "2"}. API-flow 401 handler: returns JSON for native mobile / SPA
#    fetch clients that can't handle a redirect to the portal login.
#    The client should treat 401 as "obtain a new JWT, retry".
location @portal_api_401 {
    default_type application/json;
    add_header WWW-Authenticate "Bearer realm=\\"portal\\"" always;
    return 401 '{"status":false,"message":"Portal authentication required","login_url":"${cleanBase}/"}';
}` : "";

  const wiringExample = mode === "api"
    ? `# ${showBrowser ? "3" : "2"}. Wire the gate into your API location block.
#    REST clients (mobile / SPA fetch) get JSON 401 on failure.
#
#    location / {
#        auth_request          /__portal_auth_check;
#        auth_request_set      $portal_user  $upstream_http_x_portal_user;
#        auth_request_set      $portal_role  $upstream_http_x_portal_user_role;
#        auth_request_set      $portal_email $upstream_http_x_portal_user_email;
#        proxy_set_header      X-Portal-User  $portal_user;
#        proxy_set_header      X-Portal-Role  $portal_role;
#        proxy_set_header      X-Portal-Email $portal_email;
#        error_page 401 = @portal_api_401;
#        proxy_pass http://YOUR_UPSTREAM;
#    }`
    : mode === "browser"
    ? `# 3. Wire the gate into the protected location.
#    Drop this auth_request line into your existing \`location / { ... }\`
#    block (or wherever the app is served).
#
#    location / {
#        auth_request          /__portal_auth_check;
#        auth_request_set      $portal_user  $upstream_http_x_portal_user;
#        auth_request_set      $portal_role  $upstream_http_x_portal_user_role;
#        auth_request_set      $portal_email $upstream_http_x_portal_user_email;
#        proxy_set_header      X-Portal-User  $portal_user;
#        proxy_set_header      X-Portal-Role  $portal_role;
#        proxy_set_header      X-Portal-Email $portal_email;
#        error_page 401 = @portal_login;
#        proxy_pass http://YOUR_UPSTREAM;
#    }`
    : `# 4. Hybrid wiring: API paths under ${apiPrefix} return JSON 401;
#    everything else 302s to portal login. Use this when the same
#    hostname serves both your SPA pages and a REST API the mobile
#    app talks to.
#
#    # API endpoints — JSON 401 for fetch / mobile clients.
#    location ${apiPrefix} {
#        auth_request          /__portal_auth_check;
#        auth_request_set      $portal_user  $upstream_http_x_portal_user;
#        proxy_set_header      X-Portal-User $portal_user;
#        error_page 401 = @portal_api_401;
#        proxy_pass http://YOUR_UPSTREAM;
#    }
#
#    # Everything else (SPA / pages) — redirect to portal login.
#    location / {
#        auth_request          /__portal_auth_check;
#        auth_request_set      $portal_user  $upstream_http_x_portal_user;
#        proxy_set_header      X-Portal-User $portal_user;
#        error_page 401 = @portal_login;
#        proxy_pass http://YOUR_UPSTREAM;
#    }`;

  return `# ── Portal forward-auth gate ───────────────────────────────────
# Generated by the Smarthatch portal for ${host}
# Mode: ${mode}${mode === "hybrid" ? ` (API prefix: ${apiPrefix})` : ""}
#
# Every request hitting this server first triggers a subrequest to
# the portal's /v1/portal/auth-check endpoint. The portal validates
# the user's JWT (from Cookie OR Authorization: Bearer header) and
# either lets the request through (200) or signals 401 — which this
# config then converts to the right shape for the calling client.
#
# Works for:
#   • Browsers — JWT lives in the urltoken cookie. Requires the
#     portal cookie to be set with Domain=.your-root-domain so it
#     travels to this hostname.
#   • Mobile apps / SPAs — JWT lives in Authorization: Bearer header.
#     No cookie / domain setup needed.

${gateBlock}${loginBlock}${apiBlock}

${wiringExample}
#
# After editing: nginx -t && systemctl reload nginx`;
}

function safeHost(u) {
  try { return new URL(String(u).startsWith("http") ? u : `https://${u}`).host; }
  catch { return String(u || "your-app.example.com"); }
}
